/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListResourceAudienceRequest,
  ListResourceAudienceSecurity,
  QueryParamResourceKind,
} from "../models/operations/listresourceaudience.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildResourceAudienceQuery,
  prefetchResourceAudience,
  queryKeyResourceAudience,
  ResourceAudienceQueryData,
} from "./resourceAudience.core.js";
export {
  buildResourceAudienceQuery,
  prefetchResourceAudience,
  queryKeyResourceAudience,
  type ResourceAudienceQueryData,
};

export type ResourceAudienceQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listResourceAudience access
 *
 * @remarks
 * List who can reach one resource: the principals granted or blocked on it, and the organization-wide rules they inherit.
 */
export function useResourceAudience(
  request: ListResourceAudienceRequest,
  security?: ListResourceAudienceSecurity | undefined,
  options?: QueryHookOptions<
    ResourceAudienceQueryData,
    ResourceAudienceQueryError
  >,
): UseQueryResult<ResourceAudienceQueryData, ResourceAudienceQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildResourceAudienceQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listResourceAudience access
 *
 * @remarks
 * List who can reach one resource: the principals granted or blocked on it, and the organization-wide rules they inherit.
 */
export function useResourceAudienceSuspense(
  request: ListResourceAudienceRequest,
  security?: ListResourceAudienceSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ResourceAudienceQueryData,
    ResourceAudienceQueryError
  >,
): UseSuspenseQueryResult<
  ResourceAudienceQueryData,
  ResourceAudienceQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildResourceAudienceQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setResourceAudienceData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      resourceKind: QueryParamResourceKind;
      resourceId: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: ResourceAudienceQueryData,
): ResourceAudienceQueryData | undefined {
  const key = queryKeyResourceAudience(...queryKeyBase);

  return client.setQueryData<ResourceAudienceQueryData>(key, data);
}

export function invalidateResourceAudience(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      resourceKind: QueryParamResourceKind;
      resourceId: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "listResourceAudience",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllResourceAudience(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listResourceAudience"],
  });
}
